// Composite pattern for accordion checkboxes
export default interface Layer {
    id: string;
    layerSettings: any | undefined;
    isCheckable: boolean;
}

export class InvisibleLayer implements Layer {
    public readonly isCheckable = false;
    public id: string;
    public layerSettings: any | undefined;
    constructor(params: { id: string; layerSettings: any | undefined }) {
        this.id = params.id;
        this.layerSettings = params.layerSettings;
    }
}

export abstract class CheckableLayer implements Layer {
    public readonly isCheckable = true;
    public id: string;
    public visible: boolean;
    public label: string;
    private parent: CheckableLayer | undefined;
    public subFilters: CheckableLayer[] | undefined;
    public layerSettings: any | undefined;

    constructor(params: {
        id: string;
        visible: boolean;
        label: string;
        subFilters?: CheckableLayer[];
        layerSettings?: any;
    }) {
        this.id = params.id;
        this.visible = params.visible;
        this.label = params.label;
        this.subFilters = params.subFilters;
        this.layerSettings = params.layerSettings;
    }

    public toggleById(
        id: string,
        toggleAction?: (filter: CheckableLayer) => void
    ) {
        if (this.id === id) {
            this.toggle(toggleAction);
        } else {
            if (this.subFilters) {
                this.subFilters.forEach((subFilter) =>
                    subFilter.toggleById(id, toggleAction)
                );
            }
        }
    }

    // recursively updates subfilters
    public toggle(toggleAction?: (filter: CheckableLayer) => void) {
        this.visible = !this.visible;
        this.toggleChildren(this.visible, toggleAction);
        if (this.parent) this.parent.rippleVisibilityUp();
    }

    // Updates parent state by recursively calling updateVisibility
    public rippleVisibilityUp() {
        this.updateVisibility();
        if (this.parent) {
            this.parent.rippleVisibilityUp();
        }
    }

    // sets self's visibility depending on children
    // (i.e. if all children are hidden self should be hidden as well)
    private updateVisibility() {
        if (this.subFilters) {
            this.visible = this.subFilters.some((filter) => filter.visible);
        }
    }

    // recursively sets children's visibility based on self's visibility
    private toggleChildren(
        visibility: boolean,
        toggleAction?: (filter: CheckableLayer) => void
    ) {
        this.visible = visibility;
        if (!this.subFilters) {
            if (toggleAction) {
                toggleAction(this);
            }
        } else {
            this.subFilters.forEach((child) =>
                child.toggleChildren(visibility, toggleAction)
            );
        }
    }

    // Recursively assign parents from the root based on assigned children
    public assignParents() {
        if (!this.subFilters) return;
        this.subFilters.forEach((filter) => {
            filter.parent = this;
            filter.assignParents();
        });
    }
}

// Represents the branches of a CheckableLayer tree
export class FilterCategory extends CheckableLayer {
    public filterProperty: string | undefined;
    public generateFilterExpression:
        | {
              (
                  visibleFilters: CheckableLayer[],
                  filterProperty?: string
              ): mapboxgl.ExpressionSpecification;
          }
        | undefined;
    constructor(params: {
        id: string;
        visible: boolean;
        label: string;
        // parent?: CheckableLayer;
        subFilters?: CheckableLayer[];
        layerSettings?: any;
        filterProperty?: string;
        generateFilterExpression?: {
            (
                visibleFilters: CheckableLayer[],
                filterProperty?: string
            ): mapboxgl.ExpressionSpecification;
        };
    }) {
        super(params);
        this.filterProperty = params.filterProperty;
        this.generateFilterExpression = params.generateFilterExpression;
    }
}

// Generally represents the leaf of a CheckableLayer tree
export class Filter extends CheckableLayer {
    public color: string | undefined;
    public symbolType: "line" | "circle" | "triangle" | "square" | "other";
    public icon: React.ComponentType | undefined;
    constructor(params: {
        id: string;
        visible: boolean;
        label: string;
        subFilters?: CheckableLayer[];
        layerSettings?: any;
        color?: string;
        symbolType: "line" | "circle" | "triangle" | "square" | "other";
        icon?: React.ComponentType;
    }) {
        super(params);
        this.color = params.color;
        this.symbolType = params.symbolType;
        this.icon = params.icon;
    }
}
